import { diff_match_patch, Diff, DIFF_EQUAL } from 'diff-match-patch';
import { ItemListType, ItemListChildType, ItemChoiceType } from '../../types/common';

export type DiffStatus = 'added' | 'removed' | 'changed' | 'unchanged';

export interface ChildDiffEntry {
  status: DiffStatus;
  before: ItemListChildType | null;
  after: ItemListChildType | null;
  childValueDiff?: TextSegment[];
  extractionRuleDiff?: TextSegment[];
  outputFormatDiff?: TextSegment[];
  /** 両方に存在するが配列内の位置が変わった場合 true。位置は1始まり。 */
  moved?: boolean;
  beforeIndex?: number;
  afterIndex?: number;
}

export interface ItemDiffEntry {
  status: DiffStatus;
  before: ItemListType | null;
  after: ItemListType | null;
  parentValueDiff?: TextSegment[];
  extractionRuleDiff?: TextSegment[];
  outputFormatDiff?: TextSegment[];
  childDiffs: ChildDiffEntry[];
  /** 両方に存在するが配列内の位置が変わった場合 true。位置は1始まり。 */
  moved?: boolean;
  beforeIndex?: number;
  afterIndex?: number;
}

export interface TextSegment {
  type: 'equal' | 'insert' | 'delete';
  text: string;
}

const dmp = new diff_match_patch();

/**
 * 2つの文字列を文字単位 diff し、表示用セグメント列に変換する。
 */
export const buildTextDiffSegments = (before: string, after: string): TextSegment[] => {
  const diffs: Diff[] = dmp.diff_main(before ?? '', after ?? '');
  dmp.diff_cleanupSemantic(diffs);
  return diffs.map(([op, text]) => {
    if (op === DIFF_EQUAL) return { type: 'equal' as const, text };
    if (op === 1) return { type: 'insert' as const, text };
    return { type: 'delete' as const, text };
  });
};

const isEqualText = (a?: string | null, b?: string | null): boolean => {
  return (a ?? '') === (b ?? '');
};

/**
 * 選択肢（#3075）が同一かを判定する。順序も含めて比較する
 * （選択肢の並び順はプルダウンの表示順になるため、入れ替えも変更とみなす）。
 */
const isEqualChoices = (a?: ItemChoiceType[] | null, b?: ItemChoiceType[] | null): boolean => {
  const listA = a ?? [];
  const listB = b ?? [];
  if (listA.length !== listB.length) return false;
  return listA.every(
    (choice, idx) =>
      isEqualText(choice.label, listB[idx].label) && isEqualText(choice.value, listB[idx].value),
  );
};

/**
 * 親項目・子項目に共通する属性が同一かを判定する。
 * 項目名（parentValue / childValue）は型ごとに名前が異なるため呼び出し側で判定する。
 *
 * 判定対象から漏れると「変更したのに差分に出ない」ことになるため、
 * ItemListType / ItemListChildType に属性を追加したらここにも追加すること。
 */
const isEqualCommonAttributes = (
  before: ItemListType | ItemListChildType,
  after: ItemListType | ItemListChildType,
): boolean => {
  return (
    isEqualText(before.extractionRule, after.extractionRule) &&
    isEqualText(before.outputFormat, after.outputFormat) &&
    (before.type ?? '') === (after.type ?? '') &&
    (before.displayWidth ?? 0) === (after.displayWidth ?? 0) &&
    (before.is_active ?? true) === (after.is_active ?? true) &&
    // #2968: OCR対象フラグ
    (before.is_ocr_target ?? true) === (after.is_ocr_target ?? true) &&
    // #3175: フィールドコード
    isEqualText(before.field_code, after.field_code) &&
    // #3073: 固定値
    isEqualText(before.fixedValue, after.fixedValue) &&
    // #3075: 選択肢
    isEqualChoices(before.choices, after.choices) &&
    // #3145: 計算フィールド（計算ON/OFFと計算式）
    (before.is_calculated ?? false) === (after.is_calculated ?? false) &&
    isEqualText(before.formula, after.formula) &&
    // #2815: バリデーション（必須・文字数・数値範囲）
    (before.required ?? false) === (after.required ?? false) &&
    before.minLength === after.minLength &&
    before.maxLength === after.maxLength &&
    before.minValue === after.minValue &&
    before.maxValue === after.maxValue &&
    // #3262: 0を空欄扱い
    (before.treatZeroAsEmpty ?? false) === (after.treatZeroAsEmpty ?? false) &&
    // #3384: 読み取り精度の要注意（未設定は ON）
    (before.reviewLowConfidence ?? true) === (after.reviewLowConfidence ?? true)
  );
};

const buildChildDiff = (
  before: ItemListChildType | null,
  after: ItemListChildType | null,
): ChildDiffEntry => {
  if (before && !after) {
    return { status: 'removed', before, after: null, childDiffs: [] as never[] } as ChildDiffEntry;
  }
  if (!before && after) {
    return { status: 'added', before: null, after, childDiffs: [] as never[] } as ChildDiffEntry;
  }
  if (!before || !after) {
    return { status: 'unchanged', before, after, childDiffs: [] as never[] } as ChildDiffEntry;
  }

  const sameValue = isEqualText(before.childValue, after.childValue);
  const sameRule = isEqualText(before.extractionRule, after.extractionRule);
  const sameFormat = isEqualText(before.outputFormat, after.outputFormat);

  if (sameValue && isEqualCommonAttributes(before, after)) {
    return { status: 'unchanged', before, after, childDiffs: [] as never[] } as ChildDiffEntry;
  }

  return {
    status: 'changed',
    before,
    after,
    childValueDiff: sameValue
      ? undefined
      : buildTextDiffSegments(before.childValue ?? '', after.childValue ?? ''),
    extractionRuleDiff: sameRule
      ? undefined
      : buildTextDiffSegments(before.extractionRule ?? '', after.extractionRule ?? ''),
    outputFormatDiff: sameFormat
      ? undefined
      : buildTextDiffSegments(before.outputFormat ?? '', after.outputFormat ?? ''),
  } as ChildDiffEntry;
};

/**
 * 2つの ID 列の LCS（最長共通部分列）に含まれる ID 集合を返す。
 * - LCS 内 = 順序が変わっていない（位置は動いていない）
 * - 両方に存在するが LCS 外 = 並び替えで動いた
 *
 * 例: before=[A,B,C,D], after=[A,C,B,D] の場合、LCS は [A,B,D] または [A,C,D]。
 *     どちらでも「B か C のどちらか1件が動いた」ことを最小編集で表現できる。
 */
const computeIdLcsSet = (beforeIds: string[], afterIds: string[]): Set<string> => {
  const m = beforeIds.length;
  const n = afterIds.length;
  const dp: number[][] = Array.from({ length: m + 1 }, () => new Array<number>(n + 1).fill(0));
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (beforeIds[i - 1] === afterIds[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1] + 1;
      } else {
        dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
      }
    }
  }
  const lcs = new Set<string>();
  let i = m;
  let j = n;
  while (i > 0 && j > 0) {
    if (beforeIds[i - 1] === afterIds[j - 1]) {
      lcs.add(beforeIds[i - 1]);
      i--;
      j--;
    } else if (dp[i - 1][j] >= dp[i][j - 1]) {
      i--;
    } else {
      j--;
    }
  }
  return lcs;
};

/**
 * id → 1始まりの位置インデックスのマップを作成する（モーダル表示用）。
 * findIndex を都度呼ぶと O(n^2) になるため、ループ前に1回だけ構築して O(1) で引く。
 */
const buildIndexMap = <T extends { id: string }>(list: T[]): Map<string, number> => {
  const map = new Map<string, number>();
  list.forEach((item, idx) => map.set(item.id, idx + 1));
  return map;
};

/**
 * item_list の差分を計算する。id をキーとして親子をマッチングしつつ、
 * 配列内の位置変更（並び替え）も検出する。
 *
 * 出力順:
 * - まず after の出現順で「追加」「変更（並び替え含む）」「変更なし」を並べる
 * - 最後に before にのみ存在する「削除」項目を末尾に追加
 *
 * 並び替え判定:
 * - 両方に存在する ID 集合の LCS を計算し、LCS 外の ID を `moved: true` とする
 * - フィールド変更が無くても moved なら status: 'changed' を返す
 */
export const buildItemListDiff = (
  beforeList: ItemListType[],
  afterList: ItemListType[],
): ItemDiffEntry[] => {
  const beforeMap = new Map<string, ItemListType>();
  beforeList.forEach(item => beforeMap.set(item.id, item));
  const afterMap = new Map<string, ItemListType>();
  afterList.forEach(item => afterMap.set(item.id, item));

  // 両方に存在する ID 列だけで LCS を取って「動いていない」項目を特定する。
  // before/after のどちらか片方にしかない ID は LCS 計算から除外する。
  const beforeCommonIds = beforeList.map(i => i.id).filter(id => afterMap.has(id));
  const afterCommonIds = afterList.map(i => i.id).filter(id => beforeMap.has(id));
  const stableIds = computeIdLcsSet(beforeCommonIds, afterCommonIds);

  const beforeIndexMap = buildIndexMap(beforeList);
  const afterIndexMap = buildIndexMap(afterList);

  // after の順に並べる（新しい状態のレイアウトを基本に表示する）
  const orderedIds: string[] = [];
  const seen = new Set<string>();
  afterList.forEach(item => {
    if (!seen.has(item.id)) {
      orderedIds.push(item.id);
      seen.add(item.id);
    }
  });
  // 末尾に「削除（before のみに存在）」を追加
  beforeList.forEach(item => {
    if (!seen.has(item.id)) {
      orderedIds.push(item.id);
      seen.add(item.id);
    }
  });

  return orderedIds.map(id => {
    const before = beforeMap.get(id) ?? null;
    const after = afterMap.get(id) ?? null;
    const beforeIndex = beforeIndexMap.get(id);
    const afterIndex = afterIndexMap.get(id);

    if (before && !after) {
      return {
        status: 'removed' as DiffStatus,
        before,
        after: null,
        beforeIndex,
        childDiffs: (before.children ?? []).map(c => buildChildDiff(c, null)) as ChildDiffEntry[],
      };
    }
    if (!before && after) {
      return {
        status: 'added' as DiffStatus,
        before: null,
        after,
        afterIndex,
        childDiffs: (after.children ?? []).map(c => buildChildDiff(null, c)) as ChildDiffEntry[],
      };
    }
    if (!before || !after) {
      return {
        status: 'unchanged' as DiffStatus,
        before,
        after,
        childDiffs: [],
      };
    }

    const sameValue = isEqualText(before.parentValue, after.parentValue);
    const sameRule = isEqualText(before.extractionRule, after.extractionRule);
    const sameFormat = isEqualText(before.outputFormat, after.outputFormat);
    const sameCommon = isEqualCommonAttributes(before, after);
    const moved = !stableIds.has(id);

    // 子要素は after の順に並べ、削除を末尾に。並び替えは LCS で判定。
    const beforeChildMap = new Map<string, ItemListChildType>();
    (before.children ?? []).forEach(c => beforeChildMap.set(c.id, c));
    const afterChildMap = new Map<string, ItemListChildType>();
    (after.children ?? []).forEach(c => afterChildMap.set(c.id, c));

    const beforeCommonChildIds = (before.children ?? [])
      .map(c => c.id)
      .filter(cid => afterChildMap.has(cid));
    const afterCommonChildIds = (after.children ?? [])
      .map(c => c.id)
      .filter(cid => beforeChildMap.has(cid));
    const stableChildIds = computeIdLcsSet(beforeCommonChildIds, afterCommonChildIds);

    const beforeChildIndexMap = buildIndexMap(before.children ?? []);
    const afterChildIndexMap = buildIndexMap(after.children ?? []);

    const childOrder: string[] = [];
    const childSeen = new Set<string>();
    (after.children ?? []).forEach(c => {
      if (!childSeen.has(c.id)) {
        childOrder.push(c.id);
        childSeen.add(c.id);
      }
    });
    (before.children ?? []).forEach(c => {
      if (!childSeen.has(c.id)) {
        childOrder.push(c.id);
        childSeen.add(c.id);
      }
    });

    const childDiffs = childOrder
      .map(cid => {
        const bc = beforeChildMap.get(cid) ?? null;
        const ac = afterChildMap.get(cid) ?? null;
        const base = buildChildDiff(bc, ac);
        const childBeforeIndex = beforeChildIndexMap.get(cid);
        const childAfterIndex = afterChildIndexMap.get(cid);
        // 両方に存在し位置だけ動いた場合は unchanged → changed に格上げ
        const childMoved =
          !!bc &&
          !!ac &&
          childBeforeIndex !== undefined &&
          childAfterIndex !== undefined &&
          !stableChildIds.has(cid);
        if (base.status === 'unchanged' && childMoved) {
          return {
            ...base,
            status: 'changed' as DiffStatus,
            moved: true,
            beforeIndex: childBeforeIndex,
            afterIndex: childAfterIndex,
          };
        }
        return {
          ...base,
          moved: childMoved || undefined,
          beforeIndex: bc ? childBeforeIndex : undefined,
          afterIndex: ac ? childAfterIndex : undefined,
        };
      })
      .filter(c => c.status !== 'unchanged');

    const parentChanged = !sameValue || !sameCommon;

    if (!parentChanged && !moved && childDiffs.length === 0) {
      return {
        status: 'unchanged' as DiffStatus,
        before,
        after,
        beforeIndex,
        afterIndex,
        childDiffs: [],
      };
    }

    return {
      status: 'changed' as DiffStatus,
      before,
      after,
      beforeIndex,
      afterIndex,
      moved: moved || undefined,
      parentValueDiff: sameValue
        ? undefined
        : buildTextDiffSegments(before.parentValue ?? '', after.parentValue ?? ''),
      extractionRuleDiff: sameRule
        ? undefined
        : buildTextDiffSegments(before.extractionRule ?? '', after.extractionRule ?? ''),
      outputFormatDiff: sameFormat
        ? undefined
        : buildTextDiffSegments(before.outputFormat ?? '', after.outputFormat ?? ''),
      childDiffs,
    };
  });
};

/**
 * 差分が1件もない場合の判定（モーダル表示分岐用）。
 */
export const hasAnyItemDiff = (diffs: ItemDiffEntry[]): boolean => {
  return diffs.some(d => d.status !== 'unchanged');
};
